/**
 * The Flow Optimizer's formula metrics — ONE list for every formula editor
 * (the Flow Optimizer itself, the Drop Generator's hour and text rankings):
 * ids, names, categories and what each means in plain words. Every id is
 * derived on both trackers by `deriveFlowMetrics` (tag-rules/metric-totals),
 * so a metric listed here always has a value to score.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { FlowOptimizerMetricMeta } from "../../voluum-flow-optimizer.types";

export const FLOW_FORMULA_METRICS: FlowOptimizerMetricMeta[] = [
  { id: "impressions", name: "Impressions", category: "traffic" },
  { id: "ImCTR", name: "SMS CTR", category: "rates" },
  { id: "Click2Reg", name: "Click2Reg", category: "rates" },
  { id: "Reg2Dep", name: "Reg2Dep", category: "rates" },
  { id: "uEPV", name: "uEPV", category: "rates" },
  { id: "CPuC", name: "CPuC", category: "advanced" },
  { id: "CPL", name: "CPL", category: "advanced" },
  { id: "CPS", name: "CPS", category: "advanced" },
  { id: "visits", name: "Visits", category: "traffic" },
  { id: "uniqueVisits", name: "Unique Visits", category: "traffic" },
  { id: "clicks", name: "Clicks", category: "traffic" },
  { id: "uniqueClicks", name: "Unique Clicks", category: "traffic" },
  { id: "conversions", name: "Conversions", category: "conversions" },
  { id: "registrations", name: "Registrations", category: "conversions" },
  { id: "ftd", name: "FTD", category: "conversions" },
  { id: "revenue", name: "Revenue", category: "financial" },
  { id: "cost", name: "Cost", category: "financial" },
  { id: "profit", name: "Profit", category: "financial" },
  { id: "roi", name: "ROI", category: "rates" },
  { id: "ctr", name: "CTR", category: "rates" },
  { id: "cr", name: "CR", category: "rates" },
  { id: "cv", name: "CV", category: "rates" },
  { id: "epv", name: "EPV", category: "rates" },
  { id: "epc", name: "EPC", category: "rates" },
  { id: "epl", name: "EPL", category: "rates" },
  { id: "uEPC", name: "Unique EPC", category: "rates" },
  { id: "uctr", name: "Unique CTR", category: "advanced" },
  { id: "ucr", name: "Unique CR", category: "advanced" },
  { id: "cpv", name: "CPV", category: "advanced" },
  { id: "cpc", name: "CPC", category: "advanced" },
  { id: "ecpa", name: "eCPA", category: "advanced" },
  { id: "ecpm", name: "eCPM", category: "advanced" },
  { id: "rpm", name: "RPM", category: "advanced" },
];

export const FLOW_FORMULA_METRIC_IDS = FLOW_FORMULA_METRICS.map((m) => m.id as string);

export const FLOW_METRIC_LABELS: Record<string, string> = Object.fromEntries(
  FLOW_FORMULA_METRICS.map((m) => [m.id, m.name]),
);

/** Plain words, for the caption under a formula ("CR = conversions per 100 visits"). */
export const FLOW_METRIC_MEANING: Record<string, string> = {
  impressions: "times the offer was shown",
  ImCTR: "unique clicks per 100 impressions",
  Click2Reg: "registrations per 100 unique clicks",
  Reg2Dep: "first deposits per 100 registrations",
  uEPV: "revenue per unique visit",
  CPuC: "cost per unique click",
  CPL: "cost per registration",
  CPS: "cost per impression",
  visits: "visits",
  uniqueVisits: "visits by distinct people",
  clicks: "clicks",
  uniqueClicks: "clicks by distinct people",
  conversions: "conversions",
  registrations: "registrations",
  ftd: "first deposits",
  revenue: "money earned",
  cost: "money spent",
  profit: "revenue minus cost",
  roi: "profit per 100 of cost",
  ctr: "clicks per 100 visits",
  cr: "conversions per 100 visits",
  cv: "conversions per 100 clicks",
  epv: "revenue per visit",
  epc: "revenue per click",
  epl: "revenue per conversion",
  uEPC: "revenue per unique click",
  uctr: "unique clicks per 100 unique visits",
  ucr: "conversions per 100 unique clicks",
  cpv: "cost per visit",
  cpc: "cost per click",
  ecpa: "cost per conversion",
  ecpm: "cost per 1,000 impressions",
  rpm: "revenue per 1,000 visits",
};

/** Counts grow with how much was sent — fine to score, but they mostly re-elect what is already used most. */
export const FLOW_VOLUME_METRICS = new Set(
  FLOW_FORMULA_METRICS.filter((m) => m.category === "traffic" || m.category === "conversions" || m.category === "financial").map((m) => m.id as string),
);
